import { CardDataSource, ICardReaderStore } from "modules/cardReader/domain/interfaces/IUseCardReaderStore";
import { IOfflineCardData } from "modules/cardReader/domain/models/IOfflineCardData";
import { create } from "zustand";
import { enableSensor } from "./CardReaderActions";
import { subscribeWithSelector } from "zustand/middleware";
import { ReadType } from "modules/cardReader/domain/models/IReadType";
import {
  abortReadCard,
  readCard,
} from "modules/reactNativeBridge/ReactNativeBridge";
import { isReactNative } from "utils/constants";
import { IOrderPad } from "domains/cashless/aggregates/IOrderPad";
import { ICardCashless } from "domains/cashless/aggregates/ICardCashless";
import { ICardGroupDiscounts } from "domains/cashless/aggregates/ICardCashless";

export const useCardReaderStore = create<ICardReaderStore>()(


  subscribeWithSelector((set, get) => {


    let isActiveSensor: boolean = false
    const cardData = null;
    const groupDiscounts = null;
    const orderPad = null;
    const outOfDateError = null;
    const readedCard = null;
    const isClosed = undefined;


    const onReadTag = (tag: string) => {
      console.log("onReadTag", tag);
      if (isActiveSensor) {
        set({ tag });
      }
    };

    const INVALID_OFFLINE_CARD_ERROR = "Cartão sem saldo ou sem associação. Leitura inválida.";

    const isValidOfflineCardData = (data: IOfflineCardData | null | undefined): data is IOfflineCardData => {
      if (!data) return false;
      const hasBalance = typeof data.Balance === "number";
      const hasAssociationId = !!data.AssociationId;
      return hasBalance && hasAssociationId;
    };

    const readTag = async (type: ReadType, onlyUid?: boolean, disableBeep?: boolean) => {
      try {
        set(() => ({
          isActiveSensor: true,
          readType: type,
          tag: null,
          cardDetected: false,
          offlineCardData: null,
          readError: null,
        }));
        const mifare = await readCard(onlyUid, disableBeep);
        if (!mifare) {
          throw new Error("Falha na leitura do cartão");
        }
        console.log("mifare", mifare);
        if (!mifare.success) {
          set({ readError: mifare.message ?? null });
          throw new Error(mifare.message ?? "Falha na leitura do cartão");
        }
        const offlineData = mifare.offlineCardData ?? null;
        if (offlineData && !isValidOfflineCardData(offlineData)) {
          set({
            readError: INVALID_OFFLINE_CARD_ERROR,
            offlineCardData: null,
            tag: mifare.uid ?? null,
            isClosed: mifare.isClosed ?? undefined,
          });
          throw new Error(INVALID_OFFLINE_CARD_ERROR);
        }
        console.log("mifare success", mifare);
        set({
          readError: mifare.message ?? null,
          offlineCardData: offlineData,
          tag: mifare.uid ?? null,
          isClosed: mifare.isClosed ?? undefined,
        });
      } catch (error) {
        throw error;
      } finally {
        if (!get().isOfflineRechargeRecordMode) {
          abortReadCard();
        }
      }
    };

    const setIsClosed = (isClosed: boolean | undefined) => {
      set({ isClosed: isClosed ?? undefined });
    }

    const onReadError = (readError: string) => {
      set({ readError });
    };

    const onOfflineCardData = (offlineCardData: IOfflineCardData) => {
      if (offlineCardData) {
        if (!isValidOfflineCardData(offlineCardData)) {
          set({ readError: INVALID_OFFLINE_CARD_ERROR, offlineCardData: null });
          return;
        }
        const [doc, countryCode] = (offlineCardData.Document ?? "").split("#");
        const cardData = {
          ...offlineCardData,
          Document: doc,
          CountryCode: countryCode
        }
        set({ offlineCardData: cardData, readError: null });
      }
    };

    const onRemovedCard = () => {
      set({ cardDetected: false, tag: null });
    };

    const onDetectedCard = () => {
      set({ cardDetected: true });
    };

    if (isReactNative) {
      /**
       * onDetectedCard: { type: 'DETECTED_START' | 'DETECTED_UID', uid?, timestamp }
       * onReadTag: { uid, timestamp }
       * onOfflineCardData: { uid, data, timestamp }
       * onReadError: { message, timestamp }
       * onRemovedCard: { type: 'CARD_REMOVED', message, timestamp }
       * 
       */
      window.RNBridge.register("onDetectedCard", onDetectedCard);
      window.RNBridge.register("onReadTag", onReadTag);
      window.RNBridge.register("onOfflineCardData", onOfflineCardData);
      window.RNBridge.register("onReadError", onReadError);
      window.RNBridge.register("onRemovedCard", onRemovedCard);
    } else {
      if (!window.setCardValue) {
        window.setCardValue = onReadTag;
      }
      if (!window.setReadError) {
        window.setReadError = onReadError;
      }
      if (!window.onRemovedCard) {
        window.onRemovedCard = onRemovedCard;
      }
      if (!window.onDetectedCard) {
        window.onDetectedCard = onDetectedCard;
      }
      if (!window.setFullCardData) {
        window.setFullCardData = onOfflineCardData;
      }
    }

    const enableSensor = async (type: ReadType, cashlessCode?: string) => {
      isActiveSensor = true;
      set(() => ({
        readType: type,
        tag: null,
        cardDetected: false,
        offlineCardData: null,
        readError: null,
      }));
      //return enableSensor(cashlessCode);
    }

    const disableSensor = () => {
      isActiveSensor = false;
      set(() => ({
        tag: null,
        cardDetected: false,
        offlineCardData: null,
        readError: null,
        isOfflineRechargeRecordMode: false,
      }));
      abortReadCard();
    }

    const clearCardReader = () => {
      set(() => ({
        tag: null,
        cardDetected: false,
        offlineCardData: null,
        readError: null,
      }));
    }



    const setCardData = (cardData: ICardCashless | null) => {
      set({ cardData });
    }
    const setGroupDiscounts = (groupDiscounts: ICardGroupDiscounts[] | null) => {
      set({ groupDiscounts });
    }
    const setOrderPad = (orderPad: IOrderPad | null) => {
      set({ orderPad });
    }
    const setOutOfDateError = (outOfDateError: string | null) => {
      set({ outOfDateError });
    }
    const setReadedCard = (readedCard: string | null) => {
      set({ readedCard });
    };
    const setCardDataSource = (cardDataSource: CardDataSource) => {
      set({ cardDataSource });
    };
    const setOfflineRechargeRecordMode = (value: boolean) => {
      set({ isOfflineRechargeRecordMode: value });
    };

    const setOfflineCardData = (offlineCardData: IOfflineCardData) => {
      set({ offlineCardData });
    };

    return {
      tag: null,
      cardDetected: false,
      offlineCardData: null,
      readError: null,
      isActiveSensor,
      readType: null,
      cardDataSource: null,
      isOfflineRechargeRecordMode: false,
      onReadTag,
      readTag,
      enableSensor,
      disableSensor,
      clearCardReader,
      cardData,
      groupDiscounts,
      orderPad,
      outOfDateError,
      readedCard,
      setCardData,
      setGroupDiscounts,
      setOrderPad,
      setOutOfDateError,
      setReadedCard,
      setCardDataSource,
      setOfflineRechargeRecordMode,
      setIsClosed,
      isClosed,
      setOfflineCardData,
    };
  })
);
